import { useEffect, useState } from 'react';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import StatCard from '../../components/StatCard.jsx';
import { portfolioApi } from '../../lib/apiClient.js';
import { percent } from '../../utils/format.js';
import { buildPortfolioRiskMetricsCsv, downloadMetricsCsv } from '../../utils/metricsExport.js';

/**
 * Real risk analytics — computed server-side from MSA adjusted OHLCV
 * (services/risk.js) via POST /api/portfolio/risk. Replaces the old
 * placeholder formula + pseudo-correlation. Shows a loading state while the
 * backend fetches history and an explicit empty/error state when MSA has no
 * data for the constructed holdings.
 */
export default function RiskAnalytics({ holdings }) {
  const [metrics, setMetrics] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | empty | error

  useEffect(() => {
    let alive = true;
    if (!holdings || holdings.length === 0) {
      setMetrics(null);
      setStatus('empty');
      return () => {
        alive = false;
      };
    }
    setStatus('loading');
    portfolioApi
      .risk(holdings)
      .then((m) => {
        if (!alive) return;
        setMetrics(m);
        setStatus(m.symbols && m.symbols.length > 0 ? 'ready' : 'empty');
      })
      .catch(() => {
        if (alive) setStatus('error');
      });
    return () => {
      alive = false;
    };
  }, [holdings]);

  if (status === 'empty') {
    return (
      <Card>
        <div className="aq-empty-state">Add holdings above to see risk analytics.</div>
      </Card>
    );
  }
  if (status === 'error') {
    return (
      <Card>
        <div className="aq-empty-state">Risk analytics unavailable right now — please try again.</div>
      </Card>
    );
  }
  if (status === 'loading' || !metrics) {
    return (
      <Card>
        <div className="aq-empty-state">Computing risk analytics from server-side market history…</div>
      </Card>
    );
  }

  const symbols = metrics.symbols || holdings.map((h) => h.symbol);
  const corr = metrics.correlation || {};

  return (
    <>
      <div className="aq-grid aq-grid--4">
        <StatCard label="Volatility (ann.)" value={percent(metrics.volatility)} />
        <StatCard label="Max drawdown" value={percent(metrics.maxDrawdown)} delta={metrics.maxDrawdown} />
        <StatCard label="Concentration (HHI)" value={metrics.herfindahl} />
        <StatCard label="Avg. correlation" value={metrics.avgCorrelation} />
      </div>

      <Card>
        {metrics.unavailableReason && <p role="status">{metrics.unavailableReason}</p>}
        <p className="aq-note">{metrics.assumption} Price-adjustment basis is unverified; no FX conversion is applied.</p>
        <div className="aq-metric-row" style={{ marginBottom: 16 }}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => downloadMetricsCsv(
              buildPortfolioRiskMetricsCsv(metrics),
              'alphaqap-portfolio-risk-metrics.csv'
            )}
          >
            Export portfolio metrics (CSV)
          </Button>
          <span className="aq-note">Derived metrics only; no historical prices or market-data series.</span>
        </div>
        <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
          Correlation heatmap
        </h3>
        <div className="aq-heat" style={{ gridTemplateColumns: `120px repeat(${symbols.length}, 1fr)` }}>
          <div />
          {symbols.map((s) => (
            <div key={`h-${s}`} className="aq-heat-label">
              {s}
            </div>
          ))}
          {symbols.map((rowSym) => (
            <div key={`row-${rowSym}`} style={{ display: 'contents' }}>
              <div className="aq-heat-label">{rowSym}</div>
              {symbols.map((colSym) => {
                const v = rowSym === colSym ? 1 : (corr[rowSym]?.[colSym] ?? 0);
                const intensity = Math.abs(v);
                const bg = v >= 0 ? `rgba(5,126,255,${intensity})` : `rgba(206,42,42,${intensity})`;
                return (
                  <div key={`${rowSym}-${colSym}`} className="aq-heat-cell num" style={{ background: bg }}>
                    {Number(v).toFixed(2)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {metrics.missing && metrics.missing.length > 0 && (
          <p className="aq-note">
            No price history for {metrics.missing.join(', ')} — excluded from these analytics.
          </p>
        )}
      </Card>
    </>
  );
}
